// ─── DOCUMENTOS DE EMBARQUE POR ORDEN ────────────────────────────────────────
// Factura comercial, packing list y ficha técnica guardados dentro de cada orden
// (backend: /api/ordenes/{no}/documentos, archivos en GridFS de Mongo).
// Fase 1: subir, ver, descargar y borrar. La lectura automática de datos viene después.
import { useEffect, useState } from "react";
import * as XLSX from "xlsx";
import { api } from "../auth";
import RevisionLectura from "./RevisionLectura";

export const TIPOS_DOC = [
  { id: "factura", label: "Factura comercial", icon: "🧾" },
  { id: "packing", label: "Packing list", icon: "📦" },
  { id: "ficha", label: "Ficha técnica", icon: "🧵" },
  { id: "bl", label: "B/L (conocimiento de embarque)", icon: "🚢" },
  { id: "otro", label: "Otros documentos", icon: "📎" },
];
const ACEPTA = ".pdf,.jpg,.jpeg,.png,.webp,.heic,.xlsx,.xls,.docx,.doc";
const TIPOS_PRINCIPALES = TIPOS_DOC.filter((t) => t.id !== "otro");
const etiquetaCorta = { factura: "Factura", packing: "PL", ficha: "Ficha", bl: "B/L", otro: "Otro" };
const tiposDe = (d) => (Array.isArray(d.tipos) && d.tipos.length ? d.tipos : [d.tipo || "otro"]);
const esExcel = (nombre) => /\.(xlsx|xls)$/i.test(nombre || "");

// Reconoce por el nombre de la hoja qué documento es (PL, Invoice, Ficha…).
const PATRONES_HOJA = {
  packing: /packing|^\s*p\.?\s*l\.?\s*$|^pl[\s_-]|empaque|lista de empaque|detalle de rollos|roll ?list/i,
  factura: /invoice|factura|^\s*c\.?\s*i\.?\s*$|^ci[\s_-]|commercial|proforma|^\s*inv\b|^\s*pi\b/i,
  ficha: /ficha|spec|tds|technical|t[eé]cnic|data ?sheet|fabric ?info|quality|fds/i,
  bl: /^\s*b\.?\s*\/?\s*l\.?\s*$|^bl[\s_-]|bill\s*of\s*lading|lading|conocimiento|^hbl|^mbl/i,
};
export function clasificarHojas(nombres) {
  const hojas = { factura: [], packing: [], ficha: [], bl: [] };
  for (const n of nombres || []) {
    for (const [tipo, re] of Object.entries(PATRONES_HOJA)) {
      if (re.test(n)) {
        hojas[tipo].push(n);
        break;
      }
    }
  }
  return hojas;
}
const leerHojas = async (fuente) => {
  const buf = fuente instanceof Blob ? await fuente.arrayBuffer() : fuente;
  return XLSX.read(buf, { type: "array", bookSheets: true }).SheetNames || [];
};
const MAX_MB = 20;

const tamano = (b) => (b >= 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);
const fechaCorta = (iso) => {
  const d = iso ? new Date(iso) : null;
  return d && !isNaN(d) ? d.toLocaleDateString("es-MX", { day: "2-digit", month: "2-digit", year: "numeric" }) : "";
};
const mensajeError = (e) => /"detail":"([^"]+)"/.exec(String(e?.message || ""))?.[1] || "No se pudo completar. Intenta de nuevo.";

export default function DocumentosOrden({ noOrden, proveedor, articulos = [], calcularFacturaCxP, onClose, onCambio, onEmbarque }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subiendo, setSubiendo] = useState(null); // tipo que se está subiendo
  const [error, setError] = useState("");
  // Panel para decir qué documentos trae un archivo (nuevo o ya subido).
  // { archivo?: File, doc?: documento existente, tipos: [], hojas: {}, nombresHojas: [] }
  const [clasif, setClasif] = useState(null);
  // Lectura automática (IA): la última guardada y la que se está revisando.
  const [lectura, setLectura] = useState(null);
  const [leyendo, setLeyendo] = useState(false);
  const [revisando, setRevisando] = useState(false);
  const [aviso, setAviso] = useState("");

  const ruta = `/ordenes/${encodeURIComponent(noOrden)}/documentos`;

  useEffect(() => {
    let vivo = true;
    (async () => {
      try {
        const data = await api.get(ruta);
        if (vivo) setDocs(Array.isArray(data?.documentos) ? data.documentos : []);
      } catch (e) {
        if (vivo) setError("No se pudieron cargar los documentos.");
      }
      if (vivo) setLoading(false);
    })();
    (async () => {
      try {
        const data = await api.get(`/ordenes/${encodeURIComponent(noOrden)}/lectura`);
        if (vivo) setLectura(data?.lectura || null);
      } catch (e) {
        // sin lectura previa
      }
    })();
    return () => {
      vivo = false;
    };
  }, [ruta, noOrden]);

  const leer = async () => {
    setError("");
    setAviso("");
    setLeyendo(true);
    try {
      const l = await api.post(`/ordenes/${encodeURIComponent(noOrden)}/leer-documentos`, {});
      setLectura(l);
      setRevisando(true);
    } catch (e) {
      setError(mensajeError(e));
    }
    setLeyendo(false);
  };

  const subir = async (tipo, archivos) => {
    const lista = Array.from(archivos || []);
    if (!lista.length) return;
    setError("");
    const grandes = lista.filter((a) => a.size > MAX_MB * 1024 * 1024);
    if (grandes.length) return setError(`"${grandes[0].name}" pesa más de ${MAX_MB} MB.`);
    setSubiendo(tipo);
    const nuevos = [];
    for (const a of lista) {
      try {
        nuevos.push(await api.upload(ruta, a, { tipo }));
      } catch (e) {
        setError(`${a.name}: ${mensajeError(e)}`);
      }
    }
    setDocs((d) => [...d, ...nuevos]);
    setSubiendo(null);
    if (nuevos.length) onCambio?.();
  };

  const abrir = async (doc, descargar = false) => {
    // La ventana se abre antes del fetch para que el navegador no la bloquee.
    const ventana = descargar ? null : window.open("", "_blank");
    try {
      const blob = await api.blob(`/documentos/${doc.id}`);
      const url = URL.createObjectURL(blob);
      if (ventana) {
        ventana.location.href = url;
      } else {
        const a = document.createElement("a");
        a.href = url;
        a.download = doc.nombre;
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      ventana?.close();
      setError("No se pudo abrir el documento.");
    }
  };

  // Archivo combinado: si es Excel se leen las hojas y se propone qué es cada una.
  const prepararCombinado = async (archivo, doc) => {
    setError("");
    if (archivo && archivo.size > MAX_MB * 1024 * 1024) return setError(`"${archivo.name}" pesa más de ${MAX_MB} MB.`);
    const nombre = archivo?.name || doc?.nombre;
    let nombresHojas = [];
    let hojas = doc?.hojas || {};
    let tipos = doc ? tiposDe(doc).filter((t) => t !== "otro") : [];
    if (esExcel(nombre)) {
      try {
        nombresHojas = await leerHojas(archivo || (await api.blob(`/documentos/${doc.id}`)));
        if (!doc || !Object.keys(hojas).length) {
          hojas = clasificarHojas(nombresHojas);
          const detectados = TIPOS_PRINCIPALES.map((t) => t.id).filter((t) => hojas[t]?.length);
          if (detectados.length) tipos = detectados;
        }
      } catch (e) {
        // Si no se puede leer, se clasifica a mano.
      }
    }
    setClasif({ archivo, doc, tipos, hojas, nombresHojas });
  };

  const guardarClasificacion = async () => {
    const { archivo, doc, tipos, hojas } = clasif;
    if (!tipos.length) return setError("Marca al menos un documento.");
    const hojasLimpias = Object.fromEntries(tipos.filter((t) => hojas[t]?.length).map((t) => [t, hojas[t]]));
    setError("");
    setSubiendo("combinado");
    try {
      if (archivo) {
        const nuevo = await api.upload(ruta, archivo, { tipos: tipos.join(","), hojas: JSON.stringify(hojasLimpias) });
        setDocs((d) => [...d, nuevo]);
      } else {
        const act = await api.post(`/documentos/${doc.id}/clasificar`, { tipos, hojas: hojasLimpias });
        setDocs((d) => d.map((x) => (x.id === act.id ? act : x)));
      }
      setClasif(null);
      onCambio?.();
    } catch (e) {
      setError(mensajeError(e));
    }
    setSubiendo(null);
  };

  const borrar = async (doc) => {
    const varios = tiposDe(doc).length > 1 ? " Es un archivo combinado: se quita de todas sus secciones." : "";
    if (!window.confirm(`¿Borrar "${doc.nombre}" de la orden ${noOrden}?${varios} No se puede deshacer.`)) return;
    try {
      await api.del(`/documentos/${doc.id}`);
      setDocs((d) => d.filter((x) => x.id !== doc.id));
      onCambio?.();
    } catch (e) {
      setError(mensajeError(e));
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-start sm:items-center justify-center p-3 overflow-y-auto"
      onClick={() => !subiendo && !leyendo && !revisando && onClose()}
      data-testid="docs-orden-modal"
    >
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl w-full max-w-3xl p-5 space-y-3 my-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-xs font-semibold text-teal-700 uppercase tracking-wider">Documentos de embarque</p>
            <p className="text-sm text-slate-700 mt-0.5">
              Orden <span className="font-semibold">{noOrden}</span>
              {proveedor ? ` · ${proveedor}` : ""}
            </p>
            {articulos.length > 1 && (
              <p className="text-[11px] text-slate-500 mt-0.5" data-testid="docs-articulos">
                Estos documentos aplican a los {articulos.length} artículos de la orden:{" "}
                {articulos.map((a) => a.tela || a.ref || "—").join(" · ")}
              </p>
            )}
          </div>
          <button onClick={onClose} disabled={!!subiendo || leyendo} className="text-slate-400 hover:text-slate-600 text-2xl leading-none px-2" title="Cerrar">
            ×
          </button>
        </div>

        {error && <p className="text-xs text-rose-600 font-medium">{error}</p>}

        {loading ? (
          <p className="text-sm text-slate-400 py-6 text-center">Cargando…</p>
        ) : (
          <div className="space-y-3">
            <div className="border border-violet-200 bg-violet-50/50 rounded-xl p-3 flex items-center justify-between gap-2 flex-wrap" data-testid="docs-lectura">
              <div>
                <p className="text-sm font-semibold text-slate-700">🤖 Leer documentos automáticamente</p>
                <p className="text-[11px] text-slate-500">
                  {leyendo
                    ? "Leyendo factura, packing list, ficha y B/L… puede tardar 1 a 3 minutos."
                    : lectura
                    ? `Última lectura: ${fechaCorta(lectura.creado)}. Puedes revisarla otra vez o volver a leer si subiste documentos nuevos.`
                    : "Saca cantidades embarcadas, colores, fechas, composición, ancho y peso, y te los muestra para que confirmes antes de actualizar."}
                </p>
                {aviso && <p className="text-[11px] text-emerald-700 font-medium mt-0.5">{aviso}</p>}
              </div>
              <div className="flex gap-2">
                {lectura && !leyendo && (
                  <button
                    type="button"
                    onClick={() => setRevisando(true)}
                    className="text-xs font-semibold px-3 py-1.5 rounded-lg border border-violet-300 text-violet-700 hover:bg-violet-50"
                    data-testid="docs-ver-lectura"
                  >
                    Revisar última lectura
                  </button>
                )}
                <button
                  type="button"
                  onClick={leer}
                  disabled={leyendo || !!subiendo || docs.length === 0}
                  className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-50"
                  data-testid="docs-leer"
                  title={docs.length === 0 ? "Primero sube documentos" : ""}
                >
                  {leyendo ? "Leyendo…" : lectura ? "Volver a leer" : "Leer documentos"}
                </button>
              </div>
            </div>
            <div className="border border-dashed border-teal-300 bg-teal-50/40 rounded-xl p-3 flex items-center justify-between gap-2 flex-wrap">
              <div>
                <p className="text-sm font-semibold text-slate-700">📚 ¿Un solo archivo con varios documentos?</p>
                <p className="text-[11px] text-slate-500">
                  Por ejemplo, un Excel con hojas PL, Invoice y Ficha. Se guarda una vez y aparece en cada sección.
                </p>
              </div>
              <label
                className={`text-xs font-semibold px-3 py-1.5 rounded-lg cursor-pointer whitespace-nowrap ${
                  subiendo ? "opacity-50 pointer-events-none" : "bg-teal-600 text-white hover:bg-teal-700"
                }`}
              >
                {subiendo === "combinado" ? "Subiendo…" : "⬆ Subir archivo combinado"}
                <input
                  type="file"
                  accept={ACEPTA}
                  className="hidden"
                  data-testid="docs-subir-combinado"
                  onChange={(e) => {
                    const a = e.target.files?.[0];
                    e.target.value = "";
                    if (a) prepararCombinado(a);
                  }}
                />
              </label>
            </div>
            {TIPOS_DOC.map((t) => {
              const deTipo = docs.filter((d) => tiposDe(d).includes(t.id));
              return (
                <div key={t.id} className="border border-slate-200 rounded-xl p-3" data-testid={`docs-seccion-${t.id}`}>
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <p className="text-sm font-semibold text-slate-700">
                      {t.icon} {t.label}
                      <span className={`ml-2 text-[11px] font-semibold px-1.5 py-0.5 rounded ${deTipo.length ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                        {deTipo.length ? `${deTipo.length} ${deTipo.length === 1 ? "archivo" : "archivos"}` : "Pendiente"}
                      </span>
                    </p>
                    <label
                      className={`text-xs font-semibold px-3 py-1.5 rounded-lg border cursor-pointer whitespace-nowrap ${
                        subiendo ? "opacity-50 pointer-events-none" : "border-teal-300 text-teal-700 hover:bg-teal-50"
                      }`}
                    >
                      {subiendo === t.id ? "Subiendo…" : "⬆ Subir"}
                      <input
                        type="file"
                        multiple
                        accept={ACEPTA}
                        className="hidden"
                        data-testid={`docs-subir-${t.id}`}
                        onChange={(e) => {
                          subir(t.id, e.target.files);
                          e.target.value = "";
                        }}
                      />
                    </label>
                  </div>
                  {deTipo.length > 0 && (
                    <ul className="mt-2 divide-y divide-slate-100">
                      {deTipo.map((d) => (
                        <li key={d.id} className="flex items-center justify-between gap-2 py-1.5 text-xs">
                          <span className="min-w-0 flex items-center gap-1.5">
                            <button type="button" onClick={() => abrir(d)} className="text-left text-slate-700 hover:text-teal-700 hover:underline truncate" title="Ver">
                              {d.nombre}
                            </button>
                            {d.hojas?.[t.id]?.length > 0 && (
                              <span className="text-[10px] text-slate-500 whitespace-nowrap">hoja {d.hojas[t.id].join(", ")}</span>
                            )}
                            {tiposDe(d).length > 1 && (
                              <span
                                className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-sky-100 text-sky-700 whitespace-nowrap"
                                title="Este archivo trae varios documentos"
                              >
                                combinado: {tiposDe(d).map((x) => etiquetaCorta[x]).join(" + ")}
                              </span>
                            )}
                          </span>
                          <span className="flex items-center gap-1 shrink-0 text-slate-400">
                            <span className="hidden sm:inline">
                              {tamano(d.tamano)} · {fechaCorta(d.subido)}
                            </span>
                            <button
                              type="button"
                              onClick={() => prepararCombinado(null, d)}
                              className="px-1 hover:text-teal-600"
                              title="¿Qué documentos trae este archivo?"
                              data-testid={`docs-clasificar-${d.id}`}
                            >
                              🏷
                            </button>
                            <button type="button" onClick={() => abrir(d, true)} className="px-1 hover:text-teal-600" title="Descargar">
                              ⬇
                            </button>
                            <button type="button" onClick={() => borrar(d)} className="px-1 hover:text-rose-600" title="Borrar" data-testid={`docs-borrar-${d.id}`}>
                              🗑
                            </button>
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              );
            })}
          </div>
        )}
        {clasif && (
          <div className="border-2 border-teal-400 rounded-xl p-3 space-y-2 bg-white" data-testid="docs-clasificar-panel">
            <p className="text-sm font-semibold text-slate-800">
              ¿Qué documentos trae <span className="text-teal-700">{clasif.archivo?.name || clasif.doc?.nombre}</span>?
            </p>
            {clasif.nombresHojas.length > 0 ? (
              <p className="text-[11px] text-slate-500">
                Hojas del Excel: {clasif.nombresHojas.join(" · ")}. Marqué lo que reconocí por el nombre de cada hoja; corrígelo si hace
                falta.
              </p>
            ) : (
              <p className="text-[11px] text-slate-500">Marca los documentos que vienen dentro de este archivo.</p>
            )}
            <div className="space-y-1.5">
              {TIPOS_PRINCIPALES.map((t) => {
                const marcado = clasif.tipos.includes(t.id);
                return (
                  <div key={t.id} className="flex items-center gap-2 flex-wrap text-sm">
                    <label className="flex items-center gap-2 min-w-[170px] cursor-pointer">
                      <input
                        type="checkbox"
                        checked={marcado}
                        data-testid={`docs-clasif-${t.id}`}
                        onChange={(e) =>
                          setClasif((c) => ({
                            ...c,
                            tipos: e.target.checked ? [...c.tipos, t.id] : c.tipos.filter((x) => x !== t.id),
                          }))
                        }
                      />
                      {t.icon} {t.label}
                    </label>
                    {marcado && clasif.nombresHojas.length > 0 && (
                      <select
                        multiple
                        value={clasif.hojas[t.id] || []}
                        onChange={(e) =>
                          setClasif((c) => ({
                            ...c,
                            hojas: { ...c.hojas, [t.id]: Array.from(e.target.selectedOptions).map((o) => o.value) },
                          }))
                        }
                        className="border border-slate-300 rounded-lg text-xs px-1 py-0.5 min-w-[160px]"
                        size={Math.min(3, clasif.nombresHojas.length)}
                        title="Hoja(s) donde viene este documento (Ctrl/⌘ para varias)"
                      >
                        {clasif.nombresHojas.map((h) => (
                          <option key={h} value={h}>
                            {h}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setClasif(null)} disabled={!!subiendo} className="text-xs font-medium text-slate-500 px-3 py-2">
                Cancelar
              </button>
              <button
                type="button"
                onClick={guardarClasificacion}
                disabled={!!subiendo || !clasif.tipos.length}
                data-testid="docs-clasif-guardar"
                className="text-xs font-semibold px-4 py-2 rounded-lg bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-50"
              >
                {subiendo ? "Guardando…" : clasif.archivo ? "Subir" : "Guardar"}
              </button>
            </div>
          </div>
        )}

        {revisando && lectura && (
          <RevisionLectura
            noOrden={noOrden}
            lectura={lectura}
            articulosOrden={articulos}
            calcularFacturaCxP={calcularFacturaCxP}
            onClose={() => setRevisando(false)}
            onAplicado={(res) => {
              setRevisando(false);
              setAviso(
                `✅ Aplicado: ${res.articulos} ${res.articulos === 1 ? "artículo" : "artículos"} con datos de embarque` +
                  (res.facturas_cxp ? ` y ${res.facturas_cxp} ${res.facturas_cxp === 1 ? "factura" : "facturas"} de Cuentas por Pagar.` : ".")
              );
              onEmbarque?.();
            }}
          />
        )}

        <p className="text-[11px] text-slate-400">
          PDF, foto, Excel o Word, hasta {MAX_MB} MB por archivo. Puedes subir varios a la vez. La lectura automática lee PDF, fotos
          (JPG/PNG) y Excel; los Word se guardan pero no se leen.
        </p>
      </div>
    </div>
  );
}
